import { Card, message, PageHeader, Space } from "antd";
import LayoutCss from "layout/layout.module.scss";
import { useSelector } from "react-redux";
import {
  selectAvailableClients,
  selectUserRoles,
} from "redux/slices/userSlice";
import "./submitToBilling.scss";
import { useEffect, useRef, useState } from "react";
import BillingComponent from "./billingComponent";
import BillingDetails from "./billingDetails";
import { getBillHistory } from "./billingComponents/billingComponentsHelper";
import BillingApproval from "../../../redux/models/billingApproval";
import K from "../../../utilities/constants";
import AgGridUtility from "../../../utilities/agGridUtility";
import { isPermissionPresent } from "../../../utilities/generalUtility";

export default function SubmitToBilling() {
  const clients = useSelector(selectAvailableClients);
  const userRoles = useSelector(selectUserRoles);

  const [selectedBilling, setSelectedBilling] = useState();

  const canEditBilling = isPermissionPresent(
    K.Permissions.EditSubmitToBillings,
    userRoles,
  );

  const billedAmountRef = useRef(null);
  const scrollPositionRef = useRef(0);

  useEffect(() => {
    window.scrollTo({
      top: selectedBilling ? 0 : scrollPositionRef.current,
      behavior: "instant",
    });
  }, [selectedBilling]);

  const navigateBack = () => {
    setSelectedBilling(null);
  };

  const toggleEdit = () => {
    setSelectedBilling((prev) => ({ ...prev, isEditMode: !prev.isEditMode }));
  };

  const onBillingSelected = async (data) => {
    scrollPositionRef.current = window.scrollY;
    const history = await getBillHistory({
      type: data.type,
      billingId: data.details.id,
    });
    setSelectedBilling((prev) => ({
      ...prev,
      ...data,
      isEditMode: false,
      isConverting: false,
      history,
    }));
  };

  const editAmount = async () => {
    const amount = +billedAmountRef.current.value;

    const payload = {
      edit: true,
      dublicateAmount: amount,
      currency: selectedBilling.details.convertToCurrency,
    };
    try {
      const res =
        selectedBilling.type === K.BillingType.Client
          ? await BillingApproval.editClientSubmittedBilling({
              ...payload,
              clientSubmittedBillId: selectedBilling.details.id,
            })
          : await BillingApproval.editJobSubmittedBilling({
              ...payload,
              jobSubmittedBillId: selectedBilling.details.id,
            });

      const history = await getBillHistory({
        type: selectedBilling.type,
        billingId: selectedBilling.details.id,
      });

      const updatedBilling = {
        ...selectedBilling.details,
        duplicateAmount: res.dublicateAmount,
        duplicateAmountDisplayValue: res.duplicateAmountDisplayValue,
      };

      updateBilling(updatedBilling, history, selectedBilling.gridApi);
    } catch (err) {
      message.error("Failed to edit amount.");
      console.error(err);
    }
    toggleEdit();
  };

  const convert = async () => {
    const payload = {
      from: "CAD",
      to: "USD",
      amount: selectedBilling.details.amount,
      billingApprovalId: selectedBilling.details.id,
      type: selectedBilling.type,
      edit: false,
      isIssueCredit:
        Math.sign(selectedBilling.details.dublicateAmount) === -1 ? 1 : 0,
    };

    setSelectedBilling((prev) => ({ ...prev, isConverting: true }));

    try {
      const res = await BillingApproval.currencyConversion(payload);

      const history = await getBillHistory({
        type: selectedBilling.type,
        billingId: res.id,
      });

      const updatedBilling = {
        ...selectedBilling.details,
        duplicateAmount: res.dublicateAmount,
        convertToCurrency: res.convertToCurrency,
      };

      updateBilling(updatedBilling, history, selectedBilling.gridApi);
      message.success("Conversion Complete");
    } catch (err) {
      console.error(err);
    } finally {
      setSelectedBilling((prev) => ({ ...prev, isConverting: false }));
    }
  };

  const onBillingDeleted = async () => {
    if (selectedBilling.type === K.BillingType.Client) {
      await BillingApproval.deleteClientSubmittedBill(
        selectedBilling.details.id,
      );
    } else {
      await BillingApproval.deleteJobSubmittedBill(selectedBilling.details.id);
    }

    message.success("Billing deleted successfully");
    AgGridUtility.onServerSideRowDeleted(
      selectedBilling.gridApi,
      selectedBilling.details.id,
    );
    setSelectedBilling(null);
  };

  const updateBilling = (billing, history, gridApi) => {
    setSelectedBilling((prev) => ({ ...prev, history, details: billing }));
    AgGridUtility.onServerSideRowsUpdated(gridApi, [billing]);
  };

  return (
    <>
      <PageHeader
        ghost={false}
        title="Billing Approval"
        className={`${LayoutCss.appPageHeader} ${LayoutCss.pageHeader}`}
        onBack={selectedBilling ? navigateBack : null}
      />
      <Space
        direction="vertical"
        style={{
          width: "100%",
          display: selectedBilling ? "none" : "inline-flex",
        }}
      >
        {clients.map(({ id, name }) => (
          <BillingComponent
            key={id}
            clientId={id}
            clientName={name}
            onBillingSelected={onBillingSelected}
          />
        ))}
      </Space>
      {selectedBilling && (
        <Card className="clientBillingModal">
          <BillingDetails
            clientName={selectedBilling.clientName}
            type={selectedBilling.type}
            isEditMode={selectedBilling.isEditMode}
            isReadOnly={!canEditBilling}
            showConvertButton={canEditBilling}
            billing={selectedBilling.details}
            history={selectedBilling.history}
            duplicateAmount={selectedBilling.details?.duplicateAmount}
            inputRef={billedAmountRef}
            toggleEdit={toggleEdit}
            editAmount={editAmount}
            isConverting={selectedBilling.isConverting}
            convert={convert}
            onDelete={onBillingDeleted}
            showDeleteButton={isPermissionPresent(
              K.Permissions.DeleteSubmitToBillings,
              userRoles,
            )}
          />
        </Card>
      )}
    </>
  );
}
